/**
 * **Pflichtfotos** — welche Blickwinkel bei Vorbereitung, Übergabe und
 * Rücknahme fotografiert sein müssen (Migration 0173).
 *
 * Luca, 24.09.2026: *„Bau ein, dass bei der Übergabe und bei der Rücknahme
 * immer bestimmte Fotos gemacht werden müssen. In den Einstellungen soll man
 * konfigurieren können, welche Fotos bei Übergabe, Rücknahme und Vorbereitung
 * notwendig sind, zum Beispiel vorne, hinten, links, rechts, Tank. Das würde
 * ich als Standard machen."*
 *
 * Und auf die Rückfrage, ob ein Gerät ohne Tank davon abweichen kann: *„Die
 * Pflichtfotos gelten global je Mandant und Protokollart. Zusätzlich lassen
 * sich an der Gerätegruppe Abweichungen festlegen, z. B. ‚Rüttler: kein
 * Tankfoto'."* — vererbt über die Übergruppe, wie die Bedienpflicht.
 *
 * ── Die Kette ─────────────────────────────────────────────────────────
 *
 * ```
 * Gerätegruppe → Übergruppe → Betrieb (je Protokollart)
 * ```
 *
 * **Je Blickwinkel einzeln** und in drei Zuständen: `false` — entfällt für
 * diese Gruppe, `true` — ist hier zusätzlich Pflicht, und kein Eintrag —
 * erbt. Ein Eintrag gilt für alle drei Protokollarten: „kein Tank" ist eine
 * Aussage über das Gerät, nicht über den Zeitpunkt.
 *
 * Hier steht die Regel **einmal**, und API und Telefon lesen sie beide. Eine
 * Regel, die an einer Stelle steht und an der nächsten nicht, war die
 * Ursache der elf Rechenfehler vom 19./20.09.2026.
 */
/** Die Blickwinkel, die sich zur Pflicht machen lassen — in der Reihenfolge des Schirms. */
export const BLICKWINKEL = ['vorne', 'hinten', 'links', 'rechts', 'tank', 'zaehler', 'bedienstand'];
/** Die drei Protokollarten, an denen Pflichtfotos hängen — die Kontexte von `photo`. */
export const FOTOPROTOKOLLE = ['vorbereitung', 'ausgabe', 'ruecknahme'];
/** Die Vorgabe des Betriebs, bis jemand etwas anderes einstellt — Lucas „Standard". */
export const PFLICHTFOTO_VORGABE = {
    vorbereitung: ['vorne', 'hinten', 'links', 'rechts', 'tank'],
    ausgabe: ['vorne', 'hinten', 'links', 'rechts', 'tank'],
    ruecknahme: ['vorne', 'hinten', 'links', 'rechts', 'tank'],
};
const bekannt = (x) => typeof x === 'string' && BLICKWINKEL.includes(x);
/** In der festen Reihenfolge, ohne Doppelte. */
const geordnet = (liste) => {
    const menge = new Set(liste);
    return BLICKWINKEL.filter((w) => menge.has(w));
};
/**
 * Aus dem, was in der Spalte steht, eine saubere Liste — Unbekanntes fällt weg.
 * Fehlt eine Protokollart ganz, gilt für sie die Vorgabe.
 */
export function alsPflichtfotoliste(roh) {
    const objekt = typeof roh === 'object' && roh !== null && !Array.isArray(roh)
        ? roh : null;
    const eine = (art) => {
        const wert = objekt?.[art];
        return Array.isArray(wert) ? geordnet(wert.filter(bekannt)) : [...PFLICHTFOTO_VORGABE[art]];
    };
    return { vorbereitung: eine('vorbereitung'), ausgabe: eine('ausgabe'), ruecknahme: eine('ruecknahme') };
}
/** Die Abweichung einer Gruppe aus ihrer Spalte — nur bekannte Blickwinkel mit ja/nein. */
export function alsFotoabweichung(roh) {
    if (typeof roh !== 'object' || roh === null || Array.isArray(roh))
        return {};
    const aus = {};
    for (const [w, wert] of Object.entries(roh)) {
        if (bekannt(w) && typeof wert === 'boolean')
            aus[w] = wert;
    }
    return aus;
}
/**
 * **Was an einem Blickwinkel gilt** — die Gruppe, sonst ihre Übergruppe,
 * sonst `null`: Dann entscheidet die Liste des Betriebs.
 */
export function abweichungAufloesen(gruppe, uebergruppe) {
    const aus = {};
    for (const w of BLICKWINKEL) {
        const wert = gruppe[w] ?? uebergruppe[w];
        if (wert !== undefined)
            aus[w] = wert;
    }
    return aus;
}
/**
 * **Die Pflichtfotos eines Geräts** — die Liste des Betriebs je Protokollart,
 * abzüglich dessen, was die Gruppe (oder ihre Übergruppe) streicht, zuzüglich
 * dessen, was sie zusätzlich verlangt.
 */
export function pflichtfotosFuer(betrieb, gruppe = {}, uebergruppe = {}) {
    const gilt = abweichungAufloesen(gruppe, uebergruppe);
    const eine = (art) => geordnet([
        ...betrieb[art].filter((w) => gilt[w] !== false),
        ...BLICKWINKEL.filter((w) => gilt[w] === true),
    ]);
    return { vorbereitung: eine('vorbereitung'), ausgabe: eine('ausgabe'), ruecknahme: eine('ruecknahme') };
}
/**
 * **Welche Pflichtfotos noch fehlen** — was verlangt ist, minus was da ist,
 * minus was mit Grund entfällt („Foto nicht möglich").
 */
export function fehlendePflichtfotos(verlangt, da, entfaellt = []) {
    const vorhanden = new Set();
    for (const w of da)
        if (w)
            vorhanden.add(w);
    for (const w of entfaellt)
        vorhanden.add(w);
    return verlangt.filter((w) => !vorhanden.has(w));
}
//# sourceMappingURL=pflichtfoto.js.map